import React, { useCallback } from 'react';
import { Input, Button, Tag, Typography, Tooltip, Select } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { PromptCategory } from './types';

const { Text } = Typography;
const { TextArea } = Input;

const MULTILINE_FIELD_KEYS = new Set([
    'description',
    'definition',
    'expertise',
    'task',
    'rules',
    'steps',
    'concepts',
    'descp',
    'code',
    'template',
]);

export const ARRAY_ITEM_TEMPLATES: Record<string, Record<string, unknown>> = {
    examples: { title: '', code: '', descp: '' },
};

export const CATEGORY_TEMPLATES: Record<PromptCategory, Record<string, unknown>> = {
    knowledge: {},
    tasks: {
        id: '', name: '', description: '',
        executor: 'Text2SQL',
        keywords: [],
        sql_skills: [],
    },
    skills: {
        id: '', name: '', description: '',
        version: '1.0',
        type: 'skill',
        executor: 'Text2SQL',
        role: { identity: '', expertise: '', capabilities: [''] },
        instructions: { task: '', rules: '', steps: '' },
        knowledge: {
            concepts: '',
            examples: [{ title: '', code: '', descp: '' }],
        },
        template: '{user_query}',
    },
    personas: {
        id: '', name: '',
        definition: '',
        description: '',
    },
};

// eslint-disable-next-line react-refresh/only-export-components
export function deepMergeDefaults(
    defaults: Record<string, unknown>,
    data: Record<string, unknown>,
): Record<string, unknown> {
    const result = { ...data };
    for (const key of Object.keys(defaults)) {
        if (!(key in result)) {
            result[key] = JSON.parse(JSON.stringify(defaults[key]));
        } else if (
            defaults[key] != null && typeof defaults[key] === 'object' && !Array.isArray(defaults[key]) &&
            result[key] != null && typeof result[key] === 'object' && !Array.isArray(result[key])
        ) {
            result[key] = deepMergeDefaults(
                defaults[key] as Record<string, unknown>,
                result[key] as Record<string, unknown>,
            );
        }
    }
    return result;
}

export const StructuredForm: React.FC<{
    content: Record<string, unknown>;
    onChange: (updated: Record<string, unknown>) => void;
    category?: PromptCategory;
    skillOptions?: Array<{ value: string; label: string }>;
    hiddenTopLevelFields?: string[];
}> = ({ content, onChange, category, skillOptions = [], hiddenTopLevelFields = [] }) => {
    const updateField = useCallback(
        (path: string[], value: unknown) => {
            const next = JSON.parse(JSON.stringify(content));
            let cur: Record<string, unknown> | unknown[] = next;
            for (let i = 0; i < path.length - 1; i++) {
                const seg = Array.isArray(cur) ? Number(path[i]) : path[i];
                const nextValue = cur[seg as keyof typeof cur];
                if (nextValue == null || typeof nextValue !== 'object') return;
                cur = nextValue as Record<string, unknown> | unknown[];
            }
            const lastSeg = Array.isArray(cur) ? Number(path[path.length - 1]) : path[path.length - 1];
            if (Array.isArray(cur)) {
                cur[Number(lastSeg)] = value;
            } else {
                cur[String(lastSeg)] = value;
            }
            onChange(next);
        },
        [content, onChange],
    );

    const renderFields = (obj: Record<string, unknown>, path: string[] = [], depth = 0): React.ReactNode => {
        return Object.entries(obj).map(([key, value]) => {
            const fieldPath = [...path, key];
            const keyLabel = key.replace(/_/g, ' ');
            const isTopLevel = depth === 0;

            if (
                isTopLevel &&
                (hiddenTopLevelFields.includes(key) ||
                    (key === 'id' && category !== 'knowledge') ||
                    key === 'executor' ||
                    (category === 'skills' && (key === 'version' || key === 'type')))
            ) {
                return null;
            }

            if (value != null && typeof value === 'object' && !Array.isArray(value)) {
                return (
                    <div key={key} style={{ marginBottom: 12, marginLeft: depth > 0 ? 16 : 0 }}>
                        <Text strong style={{ fontSize: 13, color: '#1d1d1f', textTransform: 'capitalize' }}>{keyLabel}</Text>
                        <div style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #f0f0f0' }}>
                            {renderFields(value as Record<string, unknown>, fieldPath, depth + 1)}
                        </div>
                    </div>
                );
            }

            if (Array.isArray(value)) {
                if (category === 'tasks' && key === 'sql_skills') {
                    return (
                        <div key={key} style={{ marginBottom: 12, marginLeft: depth > 0 ? 16 : 0 }}>
                            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Skills</Text>
                            <div style={{ marginTop: 4 }}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    placeholder="Select skills"
                                    value={value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)}
                                    onChange={(selected) => updateField(fieldPath, selected)}
                                    options={skillOptions}
                                    optionFilterProp="label"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                    );
                }

                const addItem = () => {
                    const newArr = [...value];
                    if (ARRAY_ITEM_TEMPLATES[key]) {
                        newArr.push(JSON.parse(JSON.stringify(ARRAY_ITEM_TEMPLATES[key])));
                    } else if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                        const template: Record<string, unknown> = {};
                        Object.keys(value[0]).forEach((k) => { template[k] = ''; });
                        newArr.push(template);
                    } else {
                        newArr.push('');
                    }
                    updateField(fieldPath, newArr);
                };
                const removeItem = (idx: number) => {
                    const newArr = value.filter((_: unknown, j: number) => j !== idx);
                    updateField(fieldPath, newArr);
                };

                return (
                    <div key={key} style={{ marginBottom: 12, marginLeft: depth > 0 ? 16 : 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{keyLabel}</Text>
                            <Tag color="default" style={{ fontSize: 10 }}>{value.length}</Tag>
                            <Button size="small" type="text" icon={<PlusOutlined />} onClick={addItem} style={{ fontSize: 11 }}>Add</Button>
                        </div>
                        <div style={{ marginTop: 4 }}>
                            {value.map((item: unknown, i: number) => (
                                <div key={i} style={{
                                    marginBottom: 8,
                                    padding: typeof item === 'object' && item !== null ? '8px 10px' : '0',
                                    border: typeof item === 'object' && item !== null ? '1px solid #f0f0f0' : 'none',
                                    borderRadius: 6,
                                    background: typeof item === 'object' && item !== null ? '#fafafa' : 'transparent',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 11, minWidth: 20, marginTop: 4 }}>#{i + 1}</Text>
                                        <div style={{ flex: 1 }}>
                                            {typeof item === 'string' ? (
                                                <Input
                                                    size="small"
                                                    value={item}
                                                    onChange={(e) => {
                                                        const newArr = [...value];
                                                        newArr[i] = e.target.value;
                                                        updateField(fieldPath, newArr);
                                                    }}
                                                />
                                            ) : item != null && typeof item === 'object' && !Array.isArray(item) ? (
                                                renderFields(item as Record<string, unknown>, [...fieldPath, String(i)], depth + 1)
                                            ) : (
                                                <Input size="small" value={JSON.stringify(item)} readOnly />
                                            )}
                                        </div>
                                        <Tooltip title="Remove">
                                            <Button
                                                size="small"
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => removeItem(i)}
                                                style={{ marginTop: 2 }}
                                            />
                                        </Tooltip>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            }

            if (key === 'executor') {
                return (
                    <div key={key} style={{ marginBottom: 12, marginLeft: depth > 0 ? 16 : 0 }}>
                        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{keyLabel}</Text>
                        <div style={{ marginTop: 4 }}>
                            <Tooltip title="Fixed executor type — not editable">
                                <Input value="Text2SQL" disabled style={{ fontSize: 13 }} />
                            </Tooltip>
                        </div>
                    </div>
                );
            }

            const isMultiline = typeof value === 'string' && (
                MULTILINE_FIELD_KEYS.has(key) ||
                value.includes('\n') ||
                value.length > 100
            );

            return (
                <div key={key} style={{ marginBottom: 12, marginLeft: depth > 0 ? 16 : 0 }}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{keyLabel}</Text>
                    <div style={{ marginTop: 4 }}>
                        {isMultiline ? (
                            <TextArea
                                value={String(value ?? '')}
                                onChange={(e) => updateField(fieldPath, e.target.value)}
                                autoSize={{ minRows: 3, maxRows: 20 }}
                                style={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: 12, lineHeight: 1.6 }}
                            />
                        ) : (
                            <Input
                                value={String(value ?? '')}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (typeof value === 'number') updateField(fieldPath, Number(v) || v);
                                    else if (typeof value === 'boolean') updateField(fieldPath, v === 'true');
                                    else updateField(fieldPath, v);
                                }}
                                style={{ fontSize: 13 }}
                            />
                        )}
                    </div>
                </div>
            );
        });
    };

    return <div>{renderFields(content)}</div>;
};
